import L from 'leaflet';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import type { Order, OrderTracking } from '../api/types.ts';
import { useStrings } from '../i18n/index.ts';
import { pushLayer } from '../state/router.ts';
import { Icon, type IconName } from '../ui/icons.tsx';
import { pastelFor } from '../ui/kit.tsx';
import { osmTileLayer } from '../ui/leaflet.ts';
import type { LatLng } from '../ui/route.ts';

import '../styles/orders.css';

type Position = NonNullable<OrderTracking['position']>;

const MIN_SPAN = 0.012;
/** Кадр вокруг курьера и адреса с полями; без курьера — квартал вокруг двери. */
function frame(order: Order, position: Position | null): L.LatLngBounds {
  const centerLat = position ? (position.lat + order.addressLat) / 2 : order.addressLat;
  const centerLng = position ? (position.lng + order.addressLng) / 2 : order.addressLng;
  const spanLat = position ? Math.max(Math.abs(position.lat - order.addressLat) * 2.6, MIN_SPAN) : MIN_SPAN;
  const spanLng = position ? Math.max(Math.abs(position.lng - order.addressLng) * 2.6, MIN_SPAN) : MIN_SPAN;
  return L.latLngBounds(
    [centerLat - spanLat / 2, centerLng - spanLng / 2],
    [centerLat + spanLat / 2, centerLng + spanLng / 2],
  );
}

function homeIcon(): L.DivIcon {
  const node = document.createElement('span');
  node.className = 'track-home';
  node.innerHTML =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M11.3 2.6a1 1 0 0 1 1.4 0l8 7.4c.6.6.2 1.7-.7 1.7H19v7.8a1.5 1.5 0 0 1-1.5 1.5H15a.5.5 0 0 1-.5-.5v-4.4a1 1 0 0 0-1-1h-3a1 1 0 0 0-1 1v4.4a.5.5 0 0 1-.5.5H6.5A1.5 1.5 0 0 1 5 19.5v-7.8H4c-.9 0-1.3-1.1-.7-1.7l8-7.4Z"/></svg>';
  return L.divIcon({ className: '', html: node, iconSize: [32, 32], iconAnchor: [16, 16] });
}

/** Курьер едет по карте аватаром: лицо понятнее значка велосипеда. */
function courierIcon(name: string, avatarUrl: string | null): L.DivIcon {
  const node = document.createElement('span');
  node.className = 'track-courier';
  node.style.background = pastelFor(name);
  if (avatarUrl) {
    const image = document.createElement('img');
    image.src = avatarUrl;
    image.alt = '';
    image.draggable = false;
    node.append(image);
  } else {
    node.textContent = name.slice(0, 1).toUpperCase();
  }
  return L.divIcon({ className: '', html: node, iconSize: [46, 46], iconAnchor: [23, 23] });
}

/**
 * Карта отслеживания: дом клиента, живая точка курьера и линия между ними по прямой. Кадр считается
 * по обеим точкам один раз и не прыгает на каждую позицию: его возвращает кнопка «прицел».
 */
function TrackingMap(props: {
  order: Order;
  tracking: OrderTracking | null;
  route: LatLng[];
  /** Карточка заказа карту не двигает; руками ею управляют на полном экране. */
  interactive: boolean;
  controls: (reframe: () => void) => ReactNode;
}) {
  const { order, tracking, route, interactive } = props;
  const strings = useStrings();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const courierRef = useRef<L.Marker | null>(null);
  const routeRef = useRef<L.Polyline | null>(null);
  const hadPositionRef = useRef(tracking?.position != null);

  const position = tracking?.position ?? null;
  const courierName = tracking?.courierName ?? strings.courierName;
  const courierAvatarUrl = tracking?.courierAvatarUrl ?? null;
  const latestRef = useRef({ order, position });
  latestRef.current = { order, position };

  const reframe = (animate: boolean) => {
    const latest = latestRef.current;
    mapRef.current?.fitBounds(frame(latest.order, latest.position), { animate, duration: 0.4 });
  };

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const latest = latestRef.current;
    const map = L.map(element, {
      zoomControl: false,
      dragging: interactive,
      touchZoom: interactive,
      scrollWheelZoom: interactive,
      doubleClickZoom: interactive,
      boxZoom: interactive,
      keyboard: interactive,
    });
    osmTileLayer().addTo(map);
    map.fitBounds(frame(latest.order, latest.position), { animate: false });
    L.marker([latest.order.addressLat, latest.order.addressLng], { icon: homeIcon(), interactive: false, keyboard: false }).addTo(map);
    mapRef.current = map;
    // Лист или экран ещё выезжает: размер контейнера станет окончательным после анимации.
    const resize = new ResizeObserver(() => map.invalidateSize());
    resize.observe(element);
    return () => {
      resize.disconnect();
      map.remove();
      mapRef.current = null;
      courierRef.current = null;
      routeRef.current = null;
    };
  }, [interactive]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!position) {
      courierRef.current?.remove();
      courierRef.current = null;
      return;
    }
    if (courierRef.current) courierRef.current.setLatLng([position.lat, position.lng]);
    else {
      courierRef.current = L.marker([position.lat, position.lng], {
        icon: courierIcon(courierName, courierAvatarUrl),
        title: courierName,
        interactive: false,
        keyboard: false,
        zIndexOffset: 100,
      }).addTo(map);
    }
  }, [position, courierName, courierAvatarUrl]);

  useEffect(() => {
    courierRef.current?.setIcon(courierIcon(courierName, courierAvatarUrl));
  }, [courierName, courierAvatarUrl]);

  // Курьер появился на карте: обе точки один раз берутся в кадр.
  useEffect(() => {
    const has = position != null;
    if (has && !hadPositionRef.current) reframe(true);
    hadPositionRef.current = has;
  });

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const points = route.map((point) => [point.lat, point.lng] as L.LatLngTuple);
    if (points.length < 2) {
      routeRef.current?.remove();
      routeRef.current = null;
    } else if (routeRef.current) routeRef.current.setLatLngs(points);
    else routeRef.current = L.polyline(points, { className: 'track-route', interactive: false }).addTo(map);
  }, [route]);

  return (
    <div className="track-map">
      <div ref={containerRef} className="track-map-canvas" />
      <div className="track-map-controls">{props.controls(() => reframe(true))}</div>
    </div>
  );
}

function MapRoundButton(props: { icon: IconName; label: string; onClick: () => void }) {
  return (
    <button type="button" className="track-map-button" aria-label={props.label} onClick={props.onClick}>
      <Icon name={props.icon} size={17} weight={2.4} />
    </button>
  );
}

/** Та же карта на весь экран: здесь её двигают руками. */
function TrackingMapFullScreen(props: { order: Order; tracking: OrderTracking | null; route: LatLng[]; onClose: () => void }) {
  const strings = useStrings();
  const { onClose } = props;

  useEffect(() => {
    return pushLayer(onClose);
    // Слой истории ставится один раз на жизнь карты.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="track-fullscreen" role="dialog" aria-modal="true" aria-label={strings.mapExpand}>
      <TrackingMap
        order={props.order}
        tracking={props.tracking}
        route={props.route}
        interactive
        controls={(reframe) => (
          <>
            <MapRoundButton icon="close" label={strings.close} onClick={onClose} />
            <MapRoundButton icon="locate" label={strings.mapLocate} onClick={reframe} />
          </>
        )}
      />
    </div>,
    document.body,
  );
}

/** Карта в карточке заказа; маршрут общий с полноэкранной. */
export function TrackingMapCard({ order, tracking }: { order: Order; tracking: OrderTracking | null }) {
  const strings = useStrings();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const lat = tracking?.position?.lat;
  const lng = tracking?.position?.lng;
  const { addressLat, addressLng } = order;
  // Линия на карте: от курьера к адресу по прямой.
  const route = useMemo<LatLng[]>(
    () => (lat === undefined || lng === undefined ? [] : [{ lat, lng }, { lat: addressLat, lng: addressLng }]),
    [lat, lng, addressLat, addressLng],
  );

  return (
    <div className="track-card">
      <TrackingMap
        order={order}
        tracking={tracking}
        route={route}
        interactive={false}
        controls={(reframe) => (
          <>
            <MapRoundButton icon="expand" label={strings.mapExpand} onClick={() => setIsFullscreen(true)} />
            <MapRoundButton icon="locate" label={strings.mapLocate} onClick={reframe} />
          </>
        )}
      />
      {isFullscreen && <TrackingMapFullScreen order={order} tracking={tracking} route={route} onClose={() => setIsFullscreen(false)} />}
    </div>
  );
}
